import {
  type OrganizationGeneral,
  OrganizationGeneralSchema,
} from '@quromedical/models';
import { CrudForm } from 'components/form';
import type { Field } from 'components/types';
import type { Fetcher } from 'design-system';
import type React from 'react';
import { useCallback, useState } from 'react';
import { strings } from 'strings';

import { createOrganizationGeneralRows } from './fields';

interface PersonGeneralFormCardProps {
  onValidChange: (data?: OrganizationGeneral) => void;
  showErrors: boolean;
  schemeFetcher: Fetcher<string, unknown>;
}

export const OrganizationGeneralFormCard: React.FC<
  PersonGeneralFormCardProps
> = ({ onValidChange, showErrors, schemeFetcher }) => {
  const [state, setState] = useState<Partial<OrganizationGeneral>>({
    name: '',
    schemeCode: '',
  });

  const schema = OrganizationGeneralSchema;
  const isStateValid = schema.safeParse(state);

  const onChange = useCallback(
    (data: Partial<OrganizationGeneral>) => {
      setState(data);

      // manually checking since the form doesn't seem to deal well with the conditional validation
      // schema and changing fields
      const isValid = schema.safeParse(data);

      if (!isValid) {
        onValidChange(undefined);
      } else {
        const result = data as unknown as OrganizationGeneral; // casting since data is validated

        onValidChange(result);
      }
    },
    [onValidChange, schema],
  );

  const fields = createOrganizationGeneralRows(schemeFetcher, state.type);
  const shouldShowErrors = isStateValid ? false : showErrors;

  return (
    <CrudForm
      key={state.type}
      title={strings.FormLabelPersonDetails}
      // casting to avoid complexity around then handling of the `schemeCode` field
      fields={fields as Field<OrganizationGeneral>[]}
      showSubmitButton={false}
      initialValues={state}
      validationSchema={schema}
      onChange={onChange}
      showErrors={shouldShowErrors}
      validateOnChange
      validateOnMount
      hasCard={false}
      onChangeDebounceTime={0}
    />
  );
};
